import Modal, {ModalProps} from "../../common/Modal";
import {useEffect, useState} from "react";
import {Button} from "../../common/Button";
import {addChat, addGroup, addMessage, pinToGroup} from "../../../lib/database";
import {Chat, Group, Message, userOrGroup, WorkspaceMember} from "../../../lib/types";
import { v4 as uuidv4 } from 'uuid';
import {useUserContext} from "../../../providers/UserContext";
import {BigRadio} from "../../common/BigRadio";
import {UserPicker} from "../../common/UserPicker";
import data from '@emoji-mart/data';
import Picker from "@emoji-mart/react";
import {useNavigate} from "react-router-dom";
import {extractMentions, slateToPlainText} from "../../chat/PreviewRenderer";
import {Icon} from "../../common/Icon";

export const CreateGroupModal = (props: ModalProps) => {
  const [isPrivate, setIsPrivate] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [emoji, setEmoji] = useState("😊");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const handleEmojiSelect = (emoji: any) => {
    setEmoji(emoji.native);
    setShowEmojiPicker(false); // Hide the picker after selection
  };
  const {user, activeWorkspace, fetchGroups} = useUserContext();
  const [step, setStep] = useState<"settings"|"name">("settings");
  const [selectedMembers, setSelectedMembers] = useState<userOrGroup[]>([
    {
      type: "user",
      id: user?.uid || "",
      name: user?.displayName || "",
      user: (user as unknown as WorkspaceMember)
    }
  ]);

  const handleAddMember = (member: userOrGroup) => {
    if (!selectedMembers.some((m) => m.id === member.id) && member.user) {
      setSelectedMembers([...selectedMembers, member]);
    }
  };

  const handleRemoveMember = (memberId: string) => {
    setSelectedMembers(selectedMembers.filter((m) => m.id !== memberId));
  };

  const close = () => {
    setStep("settings");
    setGroupName("");
    setEmoji("😊");
    setShowEmojiPicker(false);
    setSelectedMembers([
      {
        type: "user",
        id: user?.uid || "",
        name: user?.displayName || "",
        user: (user as unknown as WorkspaceMember)
      }
    ]);
    setIsPrivate(false);
    props.onClose();
  }
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    }
  }, []);
  const createGroup = async () => {
    // create group
    setLoading(true);
    if (!groupName || !emoji || !user || !activeWorkspace) {
      setLoading(false);
      return;
    }
    const permissions: Record<string, 'admin' | 'member'> = {};
    if (isPrivate) {
      selectedMembers.forEach((member) => {
        permissions[member.id] = member.id === user?.uid ? 'admin' : 'member';
      })
    } else {
      permissions[user?.uid || ""] = 'admin';
    }
    const groupId = uuidv4();
    const group: Group = {
      id: groupId,
      workspaceId: activeWorkspace?.id || "",
      emoji: emoji,
      name: groupName,
      visibility: isPrivate ? 'private' : 'public',
      settings: {
        whoCanPost: 'members'
      },
      timestamp: Date.now(),
      permissions,
    }

    await addGroup(group);
    window.setTimeout(() => {
      fetchGroups();
    }, 500);
    navigate(`/group/${group.id}`)
    setLoading(false);
    close();
  }
  return (
    <Modal open={props.open} onClose={close}>
      {step === "settings" ? (<div className="
      bg-gray-9 lg:bg-white
      flex items-start flex-col gap-4 w-[100vw] lg:w-[500px] h-[100vh] lg:h-auto">
        <div className={"bg-white w-full px-4 py-6 flex justify-between items-center lg:block lg:p-0 shadow-elevation-2 lg:shadow-none"}>
          <h3 className="font-style-headline-5 flex justify-start items-center">
            <button className={"inline-block mr-2"} onClick={() => {
              close();
            }}>
              <Icon name={"arrow_back"} />
            </button>
            New group
          </h3>
          <p className="hidden lg:inline-block font-style-body-xs text-gray-2 mb-0 mt-2"> Create a new group to discuss common topics among group members</p>
          <Button variant={"tertiary"} className={"!text-gray-1 lg:hidden"} onClick={() => setStep("name")}>
            Next
            <Icon name={"chevron_right"} />
          </Button>
        </div>
        <div className="w-full px-4 lg:p-0">
          <span className="font-style-label-sm">Group visibility</span>
          <div className="flex flex-col lg:flex-row w-full gap-2 justify-stretch mt-2">
            <BigRadio
              onChange={() => setIsPrivate(false)}
              checked={!isPrivate}
              name={"visibility"}
              value={"public"}
              label={"Public"}
              inactiveClass={"border-gray-5 bg-transparent"}
              activeClass={"bg-white border-gray-6"}
              description={"Visible to all workspace members."} />
            <BigRadio
              onChange={() => setIsPrivate(true)}
              value={"private"}
              name={"visibility"}
              checked={isPrivate}
              label={"Private"}
              inactiveClass={"border-gray-5 bg-transparent"}
              activeClass={"bg-white border-gray-6 lg:border-gray-1 shadow-elevation-3 lg:bg-gray-10"}
              description={"Visible to selected members only."} />
          </div>
        </div>
        <div className="px-4 lg:p-0 w-full">
          {isPrivate && (
            <UserPicker
              onSelect={handleAddMember}
              selectedMembers={selectedMembers}
              inputClass={"bg-white"}
            />
          )}
          {isPrivate && selectedMembers.length > 0 && (
            <div className="w-full mt-4">
              <span className="font-style-label-sm">Group Members</span>
              <div className="mt-2 flex flex-col">
                {selectedMembers.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-2 pr-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="avatar bg-gray-500 text-white w-8 h-8 flex justify-center items-center rounded-full">
                        <img src={member.user?.avatar} className={"w-full rounded-full"} loading="lazy"/>
                      </div>
                      <div>
                        <p className="font-style-label-sm">{member.name}</p>
                        <p className="font-style-body-xs text-gray-2">{member.user?.email}</p>
                      </div>
                    </div>
                    {user?.uid !== member.id && (<button
                      className="font-style-body-xs cursor-pointer text-gray-3 hover:text-gray-1"
                      onClick={() => handleRemoveMember(member.id)}
                    >
                      Remove
                    </button>)}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="hidden lg:flex mt-6 justify-end w-full mt-auto">
            <Button onClick={() => setStep("name")}>Next</Button>
          </div>
        </div>
      </div>) : (
        <div className={"bg-gray-9 lg:bg-white flex items-start flex-col gap-4 w-[100vw] lg:w-[500px] h-[100vh] lg:h-auto"}>
          <div
            className={"bg-white w-full px-4 py-6 flex justify-between items-center lg:block lg:p-0 shadow-elevation-2 lg:shadow-none"}>
            <h3 className="font-style-headline-5 flex justify-start items-center">
              <button className={"inline-block mr-2"} onClick={() => {
                setStep("settings");
              }}>
                <Icon name={"arrow_back"}/>
              </button>
              New group
            </h3>
            <p className="hidden lg:inline-block font-style-body-xs text-gray-2 mb-0 mt-2"> Create a new group to
              discuss common topics among group members</p>
            <Button variant={"tertiary"} className={"!text-gray-1 disabled:!text-gray-3 lg:hidden"} onClick={createGroup} disabled={loading || !groupName.length || groupName.length > 20}>
              Create group
              <Icon name={"chevron_right"}/>
            </Button>
          </div>
          <div className="flex w-full relative px-4 lg:px-0">
            <button
              className="icon h-10 bg-white flex p-1 px-2 border-r-0 items-center justify-center border-gray-6 border rounded-l-md"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
              {emoji}
            </button>
            <input
              type="text"
              placeholder="Group name"
              className="w-full bg-white h-10 border border-gray-6 rounded-r-md px-2 outline-none"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
            {showEmojiPicker && (
              <div className="absolute z-20 mt-10">
                <Picker data={data} onEmojiSelect={handleEmojiSelect}/>
              </div>
            )}
          </div>
          {isPrivate && selectedMembers.length > 0 && (
            <div className="w-full mt-4 px-4">
              <span className="font-style-label-sm">Group Members</span>
              <div className="mt-2 flex flex-col">
                {selectedMembers.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-2 pr-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="avatar bg-gray-500 text-white w-8 h-8 flex justify-center items-center rounded-full">
                        <img src={member.user?.avatar} className={"w-full rounded-full"} loading="lazy"/>
                      </div>
                      <div>
                        <p className="font-style-label-sm">{member.name}</p>
                        <p className="font-style-body-xs text-gray-2">{member.user?.email}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!isPrivate && (
            <div className="w-full mt-4 px-4 font-style-label-md text-gray-3">
              <p className={"border border-gray-6 rounded-md p-4"}>This is a public group, everyone on the workspace will have access to it.</p>
            </div>
          )}
          <div className="mt-6 justify-end w-full gap-2 hidden lg:flex">
            <Button variant={"tertiary"} onClick={() => setStep("settings")}>Back</Button>
            <Button onClick={createGroup} disabled={loading || !groupName.length || groupName.length > 20} >
              Create Group</Button>
          </div>
        </div>
        )}

    </Modal>
  )
}